/**
 * PRE-DISPATCH DEEP LINK
 *
 * The texted SMS magic link redirects the driver's PWA browser to
 *   https://drivers.trycommonplace.com/?predispatch=<TOKEN>
 *
 * On launch (web) we read that `predispatch` query param exactly once, resolve it
 * to a jobId via the PUBLIC backend route GET <backend>/predispatch/<token>/resolve
 * (no auth), and — when the checklist is not already PASSED — navigate the driver
 * straight into the PreDispatchChecklist screen for that job.
 *
 * Auth-aware: if the driver is not signed in yet when the link opens, the token is
 * persisted (AsyncStorage `pending_predispatch_token`) and the resolve+navigate runs
 * after login completes (RootNavigator runs the consumer whenever a token appears).
 *
 * The whole thing is a no-op unless PREDISPATCH_ENABLED is true. It also flips the
 * shared launch guard so the HomeScreen app-open popup does NOT also fire for the
 * same job (no double-open).
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from './api';
import { PREDISPATCH_ENABLED } from '../config/flags';

const STORAGE_KEY = 'pending_predispatch_token';
const PARAM = 'predispatch';

/**
 * Shared "checked this launch" guard. The HomeScreen app-open pre-dispatch popup
 * imports the same flag — flipping it here (when a deep link is in play) keeps the
 * popup from also auto-presenting the checklist for the same job.
 *
 * It is an object (not a bare boolean) so the reference can be shared/mutated across
 * modules; a bare exported `let` would be copied by value at import time.
 */
export const predispatchLaunchGuard = { checked: false };

// Capture the token at most once per app launch.
let captureRan = false;

/** Read the `predispatch` token from the current URL (web only). */
function readTokenFromUrl(): string | null {
  if (typeof window === 'undefined' || !window.location || !window.location.search) return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const t = params.get(PARAM);
    return t && t.trim() ? t.trim() : null;
  } catch {
    return null;
  }
}

/** Strip the `predispatch` param from the URL so a refresh does not reopen it. */
function clearUrlParam(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState || !window.location) return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(PARAM)) return;
    url.searchParams.delete(PARAM);
    const qs = url.searchParams.toString();
    window.history.replaceState({}, '', `${url.pathname}${qs ? `?${qs}` : ''}${url.hash || ''}`);
  } catch {
    /* ignore */
  }
}

/**
 * Capture the token from the URL once per launch and persist it. Safe to call
 * eagerly on app start (before auth resolves). No-op when the feature is off or
 * when there is no token in the URL.
 */
export async function capturePredispatchDeepLink(): Promise<void> {
  if (!PREDISPATCH_ENABLED || captureRan) return;
  captureRan = true;
  const token = readTokenFromUrl();
  if (!token) return;
  // A deep link is in play — claim the launch immediately so the HomeScreen app-open
  // popup defers to us (no double-open). This runs in App.tsx before auth resolves,
  // so the guard is already set by the time HomeScreen's gate effect evaluates.
  predispatchLaunchGuard.checked = true;
  try { await AsyncStorage.setItem(STORAGE_KEY, token); } catch { /* ignore */ }
  // Remove the param immediately so a manual refresh can't reopen the flow; the
  // token now lives in storage and survives the (possible) login round-trip.
  clearUrlParam();
}

/** Clear the persisted token + URL param. */
async function clearPending(): Promise<void> {
  try { await AsyncStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  clearUrlParam();
}

/**
 * Build the PUBLIC resolve URL. The authed api.ts base is like
 *   https://backend-production-3884.up.railway.app/api
 * but the resolve route lives at the root (NOT under /api), so strip a trailing
 * /api (with or without a trailing slash) before appending.
 */
function resolveUrl(token: string): string {
  const base = (process.env.EXPO_PUBLIC_API_URL || 'http://localhost:3000/api').replace(/\/api\/?$/, '');
  return `${base}/predispatch/${encodeURIComponent(token)}/resolve`;
}

/**
 * Consume any pending deep-link token: resolve it to a job and, unless the
 * checklist is already PASSED, navigate into the PreDispatchChecklist screen.
 * Always clears the token + URL param on a definitive outcome so it never reopens.
 *
 * Called by RootNavigator once a real auth token is present (so the deferred,
 * post-login case is handled). The resolve endpoint itself is public, but we only
 * navigate inside the authenticated stack, hence the auth gate at the call site.
 *
 * @param navigate  a function that performs `navigation.navigate('PreDispatchChecklist', params)`
 */
export async function consumePendingPredispatch(
  navigate: (jobId: string) => void,
): Promise<void> {
  if (!PREDISPATCH_ENABLED) return;

  let token: string | null = null;
  try { token = await AsyncStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
  if (!token) return;

  // Mark the launch as handled up front so the HomeScreen popup defers to us even
  // if the network resolve is slow.
  predispatchLaunchGuard.checked = true;

  try {
    // Public route — bypass the api instance baseURL (which is under /api) and hit
    // the root resolve endpoint directly. Use api.get with an absolute URL so axios
    // ignores baseURL; no auth header is required by the route.
    const { data } = await api.get(resolveUrl(token));
    const jobId: string | undefined = data?.jobId;
    const status: string | undefined = data?.status;

    if (jobId && status !== 'PASSED') {
      navigate(jobId);
    }
    // PASSED (done) or resolved with no job → clear and do not reopen.
    await clearPending();
  } catch {
    // Resolve failed (bad/expired token, network). Drop it so we don't retry every
    // launch and never get stuck; the in-app HomeScreen popup still covers the job.
    await clearPending();
  }
}
